import { useState, useEffect } from "react";
import { Link } from "react-router";
import toast from "react-hot-toast";

import DeleteConfirmModal from "./DeleteConfirmModal";
import api from "../../utilities/api";

const ChangesPanel = () => {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState(null);
  // { type: "accept" | "delete", id, ofid, fieldName } while the confirm modal is open
  const [pendingAction, setPendingAction] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const fetchDrafts = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/records-drafts`);
      setDrafts(response.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching drafts:", err);
      setError("Failed to load drafts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrafts();
  }, []);

  const handleAcceptAll = async () => {
    try {
      setAccepting(true);
      setShowConfirm(false);
      const response = await api.post(
        `/records-drafts/accept-all`,
      );
      setDrafts([]);
      setError(null);
      console.log(`Accepted ${response.data.accepted} drafts`);
    } catch (err) {
      console.error("Error accepting drafts:", err);
      setError("Failed to accept drafts");
    } finally {
      setAccepting(false);
    }
  };

  const handleConfirmAction = async () => {
    if (!pendingAction) return;
    const { type, id, ofid } = pendingAction;

    try {
      setBusyId(id);
      setPendingAction(null);
      if (type === "accept") {
        await api.post(`/records-drafts/${id}/accept`);
      } else {
        await api.delete(`/records-drafts/${id}`);
      }
      setDrafts((prev) => prev.filter((draft) => draft._id !== id));
      toast.success(
        type === "accept" ? `Change for ${ofid} accepted` : `Change for ${ofid} deleted`,
      );
    } catch (err) {
      console.error(`Error ${type === "accept" ? "accepting" : "deleting"} draft:`, err);
      toast.error(
        err.response?.data?.message ||
          `Failed to ${type === "accept" ? "accept" : "delete"} change`,
      );
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <p className="text-gray-500 p-4">Loading changes…</p>;
  }

  if (error) {
    return <p className="text-red-600 p-4">{error}</p>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-800">
          Pending Changes ({drafts.length})
        </h2>
        {drafts.length > 0 && (
          <button
            onClick={() => setShowConfirm(true)}
            disabled={accepting}
            className="py-2 px-4 text-gray-100 font-bold text-lg border-2 border-black bg-cyan-900 hover:bg-cyan-800 rounded-lg transition duration-300 disabled:opacity-50"
          >
            {accepting ? "Accepting…" : "Accept all"}
          </button>
        )}
      </div>

      {/* Confirmation dialog */}
      {showConfirm && (
        <div className="mb-4 border-2 border-cyan-900 rounded-lg bg-cyan-50 p-4">
          <p className="text-gray-800 font-medium mb-3">
            Accept all {drafts.length} pending changes? This will publish every
            draft and remove the &quot;Under Review&quot; tags.
          </p>
          <div className="flex gap-3">
            <button
              onClick={handleAcceptAll}
              className="py-1 px-4 text-gray-100 font-bold border-2 border-black bg-cyan-900 hover:bg-cyan-800 rounded-lg transition duration-300"
            >
              Confirm
            </button>
            <button
              onClick={() => setShowConfirm(false)}
              className="py-1 px-4 font-bold border-2 border-gray-400 bg-white hover:bg-gray-200 rounded-lg transition duration-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {drafts.length === 0 ? (
        <p className="text-gray-500">No pending changes</p>
      ) : (
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full py-2 align-middle">
            <table className="min-w-full text-left">
              <thead className="bg-cyan-900 text-white">
                <tr>
                  <th className="py-2 px-2">OFID</th>
                  <th className="py-2 px-2">Field Name</th>
                  <th className="py-2 px-2 w-64"></th>
                </tr>
              </thead>
              <tbody>
                {drafts.map((draft, i) => (
                  <tr
                    key={draft._id}
                    className={i % 2 === 0 ? "bg-slate-100" : "bg-slate-300"}
                  >
                    <td className="py-1 px-2">{draft.ofid}</td>
                    <td className="py-1 px-2">{draft.fieldName}</td>
                    <td className="py-1 px-2 text-right">
                      <div className="flex justify-end gap-2">
                        <Link
                          className="py-0 px-2 text-center text-cyan-900 font-normal text-base border-2 border-cyan-900 bg-white hover:bg-gray-300 hover:text-gray-800 rounded-lg transition duration-300"
                          to={`/list/${draft._id}/draft-edit`}
                        >
                          Show
                        </Link>
                        <button
                          type="button"
                          onClick={() =>
                            setPendingAction({
                              type: "accept",
                              id: draft._id,
                              ofid: draft.ofid,
                              fieldName: draft.fieldName,
                            })
                          }
                          disabled={busyId === draft._id || accepting}
                          className="py-0 px-2 text-center text-cyan-900 font-normal text-base border-2 border-cyan-900 bg-white hover:bg-gray-300 hover:text-gray-800 rounded-lg transition duration-300 disabled:opacity-50"
                        >
                          Accept
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setPendingAction({
                              type: "delete",
                              id: draft._id,
                              ofid: draft.ofid,
                              fieldName: draft.fieldName,
                            })
                          }
                          disabled={busyId === draft._id || accepting}
                          className="py-0 px-2 text-center text-red-700 font-normal text-base border-2 border-red-300 bg-white hover:bg-red-50 hover:border-red-500 rounded-lg transition duration-300 disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={!!pendingAction}
        onClose={() => setPendingAction(null)}
        onConfirm={handleConfirmAction}
        title={pendingAction?.type === "accept" ? "Accept Change" : "Delete Change"}
        message={
          pendingAction?.type === "accept"
            ? `Accept the pending change for "${pendingAction.ofid} – ${pendingAction.fieldName}"? This will publish the draft and remove the "Under Review" tag.`
            : `Discard the pending change for "${pendingAction?.ofid} – ${pendingAction?.fieldName}"? The draft will be deleted and the "Under Review" tag removed. This cannot be undone.`
        }
        confirmLabel={pendingAction?.type === "accept" ? "Accept" : "Delete"}
        confirmClassName={
          pendingAction?.type === "accept"
            ? "bg-cyan-900 hover:bg-cyan-800"
            : "bg-red-600 hover:bg-red-700"
        }
      />
    </div>
  );
};

export default ChangesPanel;
